import { useClient } from "../api/ClientContext";
import { useSession } from "../auth/SessionProvider";
import { Empty, Panel } from "../components/Panel";
import { CapabilityPage } from "../features/capabilities/CapabilityPage";
import { ChangeSetReviewPage } from "../features/changeSets/ChangeSetReviewPage";
import { DiagramPage } from "../features/diagram/DiagramPage";
import { ElectricalFacts } from "../features/electrical/electricalFacts";
import { AuditPage } from "../features/project/AuditPage";
import { ProjectPage } from "../features/project/ProjectPage";
import { ProjectListPage } from "../features/projects/ProjectListPage";
import { RationalePage } from "../features/rationale/rationale";
import { AtexDeterminationGate } from "../features/surfaces/atexGate";
import { SurfaceIndex } from "../features/surfaces/SurfaceIndex";
import { SurfacePage } from "../features/surfaces/SurfacePage";
import { surfaceByKey } from "../features/surfaces/surfaceSlots";
import { useAsync } from "../stores/useAsync";
import { routeHref, useRoute } from "./routing";

export function App(): JSX.Element {
  const route = useRoute();
  const client = useClient();
  const { session, canAccessProject } = useSession();
  const projectRoute = route.name !== "projects" && "projectId" in route ? route.projectId : undefined;

  return (
    <div className="app">
      <nav className="app-nav">
        <a className="brand" href={routeHref({ name: "projects" })}>PlantLifecycle</a>
        {route.name !== "projects" && "projectId" in route && (
          <a href={routeHref({ name: "project", projectId: route.projectId })}>{route.projectId}</a>
        )}
        <span className="spacer" />
        <span className="muted">API {session.api.version} · Engine {session.api.engineVersion}</span>
        <button
          type="button"
          className="ghost"
          onClick={() => { void client.logout().then(() => window.location.reload()); }}
        >
          Sign out
        </button>
      </nav>

      {route.name === "projects" && <ProjectListPage />}
      {projectRoute !== undefined && !canAccessProject(projectRoute) && <AccessDenied />}
      {route.name === "project" && canAccessProject(route.projectId) && <ProjectPage projectId={route.projectId} />}
      {route.name === "audit" && canAccessProject(route.projectId) && <AuditPage projectId={route.projectId} />}
      {route.name === "capabilities" && canAccessProject(route.projectId) && <CapabilityPage projectId={route.projectId} />}
      {route.name === "surfaces" && canAccessProject(route.projectId) && <SurfaceIndex projectId={route.projectId} />}
      {route.name === "surface" && canAccessProject(route.projectId) && (
        <SurfaceRoute projectId={route.projectId} surfaceKey={route.surface} />
      )}
      {route.name === "diagram" && canAccessProject(route.projectId) && (
        <DiagramPage projectId={route.projectId} diagramId={route.diagramId} />
      )}
      {route.name === "changeSet" && canAccessProject(route.projectId) && (
        <ChangeSetReviewPage projectId={route.projectId} changeSetId={route.changeSetId} />
      )}
    </div>
  );
}

/**
 * One surface, or a named refusal.
 *
 * The manifest is fetched HERE rather than inside the surface, because the ATEX
 * gate needs the same document and two fetches of one manifest on a single page
 * would be two chances to disagree about what the Engine published. It is fetched
 * fresh on every mount and never cached: a stale manifest is a manifest that
 * claims a capability exists after the Engine withdrew it.
 */
function SurfaceRoute({ projectId, surfaceKey }: { projectId: string; surfaceKey: string }): JSX.Element {
  const client = useClient();
  const definition = surfaceByKey(surfaceKey);
  const manifest = useAsync(() => client.capabilities(), [client]);

  if (definition === undefined) {
    return (
      <div className="page">
        <Panel title="Unknown surface" tone="warn">
          <Empty>
            This workbench defines no surface called <code>{surfaceKey}</code>.{" "}
            <a href={routeHref({ name: "surfaces", projectId })}>See the surfaces it does define</a>.
          </Empty>
        </Panel>
      </div>
    );
  }
  if (manifest.data === undefined) {
    return (
      <div className="page">
        <Panel title={definition.title}>
          <Empty>
            {manifest.error !== undefined
              ? "The Engine's manifest could not be read, so this surface cannot claim what the Engine supports."
              : "Reading the Engine's published manifest…"}
          </Empty>
        </Panel>
      </div>
    );
  }
  return (
    <>
      {definition.key === "atex" && (
        <div className="page">
          <AtexDeterminationGate manifest={manifest.data} />
        </div>
      )}
      {definition.key === "electrical" && (
        <div className="page">
          <ElectricalFacts manifest={manifest.data} projectId={projectId} />
        </div>
      )}
      {definition.key === "rationale"
        ? <RationalePage manifest={manifest.data} projectId={projectId} />
        : <SurfacePage manifest={manifest.data} projectId={projectId} surface={definition} />}
    </>
  );
}

function AccessDenied(): JSX.Element {
  return (
    <main className="page">
      <div className="banner banner-error" role="alert">
        <strong>Project unavailable</strong>
        <p>You do not have access to this project, or it is no longer available.</p>
        <a className="button" href={routeHref({ name: "projects" })}>Choose another project</a>
      </div>
    </main>
  );
}
